import React, { PureComponent, type Node } from "react";
import { connect, Dispatch } from "react-redux";
import { togglesChangeAction, persistTogglesAction } from "data/actions";
import { getDisplayName } from "data/constants";
import { type ToggleDTO } from "data/types";
import getToggles from "data/toggles.js";
import immutable from "object-path-immutable";
import queryString from "data/queryString";
import isEqual from "lodash/isEqual";
import * as s from "data/reducers/selectors";

type Props = {
    /** Current URL location */
    location?: Location,
    /** Selected Toggles from props */
    toggles?: Array<string>,
    /** Selected Toggles from Redux */
    selectedToggles?: Array<string>,
    /** Dispatch Redux actions */
    dispatch: Dispatch,
    /** Viewname from props */
    viewName?: string,
};

type State = {
    toggles: ?ToggleDTO,
};

type Config = {
    viewName?: string,
};

// will manage initially loading default localized toggles
// and changing local state based on selectedToggles
const TogglesHandler =
    ({ viewName }: Config): Node =>
    (WrappedComponent: ReactClass<any>) => {
        class PP extends PureComponent<void, Props, State> {
            static displayName = `TogglesHandler(${getDisplayName(
                WrappedComponent
            )})`;

            constructor(props: Props) {
                super(props);
                this.state = {
                    toggles: getToggles(viewName || props.viewName),
                };
            }

            componentDidMount() {
                // if first time (selectedTogglesSelector has empty state)
                /* istanbul ignore else */
                if (
                    this.props.selectedToggles === undefined &&
                    this.state.toggles !== undefined
                ) {
                    this.props.dispatch(
                        togglesChangeAction(
                            viewName || this.props.viewName,
                            this.state.toggles.toggles
                        )
                    );
                }

                this._handleTogglesFromProps();
                this._handleTogglesFromQueryString();
            }

            componentDidUpdate(prevProps: Props) {
                /* istanbul ignore else */
                if (
                    !isEqual(
                        prevProps.selectedToggles,
                        this.props.selectedToggles
                    ) &&
                    this.props.selectedToggles != null
                ) {
                    this._updateSelectedToggles(this.props.selectedToggles);
                }
            }

            _handleTogglesFromProps = () =>
                this.props.toggles && this._handleToggles(this.props.toggles);

            _handleTogglesFromQueryString = () => {
                /* istanbul ignore else */
                if (!this.props.location) {
                    return;
                }
                const qs = queryString.parse(this.props.location.search);
                /* istanbul ignore else */
                if (qs.toggles) {
                    this._handleToggles(qs.toggles.split(","));
                }
            };

            _handleToggles = (toggles: Array<string>) => {
                const availableToggles = this.state.toggles.toggles.map(
                    (t) => t.value
                );
                const validToggles = toggles.filter((t) =>
                    availableToggles.includes(t)
                );
                if (validToggles.length < 1) {
                    const msg =
                        "[TogglesHandler] Invalid toggle names provided. Please read the docs to setup properly.";
                    alert(msg);
                    console.error(
                        msg,
                        "https://serviceportal.hs.ag/docs/inPoint.Web/docs/feature-integration-miniviews.html#view-toggles"
                    );
                } else {
                    /* istanbul ignore else */
                    if (validToggles.length !== toggles.length) {
                        console.warn(
                            "[TogglesHandler] Not all provided toggles can be set",
                            "https://serviceportal.hs.ag/docs/inPoint.Web/docs/feature-integration-miniviews.html#view-toggles"
                        );
                    }
                    /* istanbul ignore else */
                    if (!isEqual(this.props.selectedToggles, validToggles)) {
                        //this._updateSelectedToggles(validToggles);
                        const newToggles =
                            this._getTogglesBySelected(validToggles);
                        this.props.dispatch(
                            persistTogglesAction(
                                viewName || this.props.viewName,
                                newToggles
                            )
                        );
                    }
                }
            };

            _getTogglesBySelected = (selectedToggles: Array<string>) => {
                const { toggles } = this.state;
                if (
                    toggles === null ||
                    toggles === undefined ||
                    toggles.toggles === null
                )
                    return;

                return toggles.toggles.map((toggle) => {
                    return immutable.set(
                        toggle,
                        "isSelected",
                        selectedToggles.includes(toggle.value)
                    );
                });
            };

            setToggles = (toggles: ?ToggleDTO) => {
                this.setState({ toggles }, () => {
                    if (toggles !== undefined && toggles !== null)
                        this.props.dispatch(
                            persistTogglesAction(
                                viewName || this.props.viewName,
                                toggles.toggles
                            )
                        );
                });
            };

            _updateSelectedToggles = (selectedToggles: Array<string>) => {
                const newToggles = this._getTogglesBySelected(selectedToggles);
                if (newToggles == null) return;
                const { toggles } = this.state;
                this.setState({
                    toggles: immutable.set(toggles, "toggles", newToggles),
                });
            };

            render() {
                return (
                    <WrappedComponent
                        {...this.props}
                        {...this.state}
                        setToggles={this.setToggles}
                    />
                );
            }
        }

        const mapStateToProps = (state, ownProps) => ({
            selectedToggles: s.selectedTogglesSelector(
                state,
                viewName || ownProps.viewName
            ),
            ...ownProps,
        });

        return connect(mapStateToProps)(PP);
    };

export default TogglesHandler;
